/**
 * The browser half of M4c's multipart upload (ADR-0006 §1): slice a local file into parts, PUT
 * them through the Worker into an R2 multipart upload, then complete. Only Jeff ever gets here —
 * the wizard renders the branch that calls this only when `me.media !== null`.
 *
 * Why parts at all: a Worker request body is capped (500 MB on Paid) and a 2 GB episode would
 * blow through it, so the whole file never transits one request. The part size is the SERVER's:
 * it comes back from create, and `planParts` must slice on exactly that boundary because the
 * route refuses a part that is not `partSize` bytes (or the remainder, for the last one).
 *
 * Why there is no resume: R2 part etags live in the multipart upload the server opened, and the
 * client cannot reproduce them after a page load. So a failed upload is not repaired — the media
 * row is deliberately LEFT `uploading` for the scheduled reaper, and "try again" starts a fresh
 * upload. That is the design, not an oversight; the only thing the client aborts is a *cancel*,
 * where the user is still on the page and we can tell the server at once.
 *
 * The pure parts (`planParts`, `uploadReduce`) are first and tested on their own; `runUpload` is
 * the thin I/O shell around them, with the network injectable so the tests never touch fetch.
 */
import type {
  MediaPart,
  MediaUploadCreateRequest,
  MediaUploadCreateResponse,
  MediaUploadCompleteResponse,
} from '@nelnela/contracts';
import { ApiError, api } from './api';

// --- pure: the plan ------------------------------------------------------------

/** One slice of the file: bytes `[start, end)`, uploaded as part `n` (1-based, as R2 counts). */
export interface PartPlan {
  n: number;
  start: number;
  end: number;
  bytes: number;
}

/**
 * Every part is exactly `partSize` except the last, which is the remainder. This has to agree
 * byte-for-byte with the server's `expectedPartBytes` — it recomputes the same arithmetic from
 * the declared size and refuses a part of the wrong length. A 0-byte file plans nothing (the
 * wizard refuses it before we are called).
 */
export function planParts(totalBytes: number, partSize: number): PartPlan[] {
  const plans: PartPlan[] = [];
  if (!(totalBytes > 0) || !(partSize > 0)) return plans;
  for (let start = 0, n = 1; start < totalBytes; start += partSize, n++) {
    const end = Math.min(start + partSize, totalBytes);
    plans.push({ n, start, end, bytes: end - start });
  }
  return plans;
}

// --- pure: the progress reducer ------------------------------------------------

export interface UploadProgress {
  totalBytes: number;
  doneBytes: number;
  parts: number;
  partsDone: number;
  /** epoch ms of the `start` action — the clock the ETA is measured against */
  startedAt: number;
  /** null until a part has landed (no rate yet) and again once every byte is up */
  etaMs: number | null;
}

export const UPLOAD_IDLE: UploadProgress = {
  totalBytes: 0,
  doneBytes: 0,
  parts: 0,
  partsDone: 0,
  startedAt: 0,
  etaMs: null,
};

export type UploadAction =
  | { type: 'start'; totalBytes: number; parts: number; at: number }
  | { type: 'part'; bytes: number; at: number }
  | { type: 'reset' };

const sameProgress = (a: UploadProgress, b: UploadProgress): boolean =>
  a.totalBytes === b.totalBytes &&
  a.doneBytes === b.doneBytes &&
  a.parts === b.parts &&
  a.partsDone === b.partsDone &&
  a.startedAt === b.startedAt &&
  a.etaMs === b.etaMs;

/**
 * Pure, and returns the SAME object when nothing changed, so the bar can be a `useSyncExternal`
 * store / `useReducer` without re-rendering on a no-op. The ETA is the naive one — the mean rate
 * so far applied to the bytes left — which is the honest estimate for a stream whose throughput
 * is whatever the uplink is doing; no smoothing, because a wrong-but-steady number reads worse
 * than one that visibly reacts.
 */
export function uploadReduce(s: UploadProgress, a: UploadAction): UploadProgress {
  let next: UploadProgress;
  switch (a.type) {
    case 'start':
      next = {
        totalBytes: a.totalBytes,
        doneBytes: 0,
        parts: a.parts,
        partsDone: 0,
        startedAt: a.at,
        etaMs: null,
      };
      break;
    case 'part': {
      const doneBytes = s.doneBytes + a.bytes;
      const remaining = s.totalBytes - doneBytes;
      const elapsed = a.at - s.startedAt;
      next = {
        ...s,
        doneBytes,
        partsDone: s.partsDone + 1,
        etaMs:
          remaining > 0 && doneBytes > 0 && elapsed > 0
            ? Math.round((elapsed / doneBytes) * remaining)
            : null,
      };
      break;
    }
    case 'reset':
      next = UPLOAD_IDLE;
      break;
  }
  return sameProgress(s, next) ? s : next;
}

// --- I/O ------------------------------------------------------------------------

/** Three at a time saturates a home uplink without starving the rest of the app's requests. */
export const UPLOAD_PARALLEL = 3;
/** Attempts per part, the first one included. */
export const PART_ATTEMPTS = 3;
/** Backoff before attempt k+1 is `PART_RETRY_BASE_MS * 2^(k-1)` — 0.5 s, then 1 s. */
export const PART_RETRY_BASE_MS = 500;

/** Raised when the caller cancelled (the wizard's Cancel, or its `beforeunload` teardown), so
 *  the wizard can stay silent instead of showing an error the user caused on purpose. */
export class UploadAbortedError extends Error {
  constructor(message = 'Upload cancelled') {
    super(message);
    this.name = 'UploadAbortedError';
  }
}

/** The three calls this needs from `api`, named so the tests can hand it a stub. */
export interface UploadApi {
  create(body: MediaUploadCreateRequest): Promise<MediaUploadCreateResponse>;
  part(
    uploadId: string,
    n: number,
    blob: Blob,
    init?: { signal?: AbortSignal },
  ): Promise<MediaPart>;
  complete(uploadId: string, parts: MediaPart[]): Promise<MediaUploadCompleteResponse>;
  abort(uploadId: string): Promise<void>;
}

const liveApi: UploadApi = {
  create: (body) => api.mediaUploadCreate(body),
  part: (uploadId, n, blob, init) => api.mediaUploadPart(uploadId, n, blob, init),
  complete: (uploadId, parts) => api.mediaUploadComplete(uploadId, parts),
  abort: (uploadId) => api.mediaUploadAbort(uploadId),
};

/** What we actually need from a `File` — so a test can be a plain object, no DOM involved. */
export interface FileLike {
  name: string;
  size: number;
  type: string;
  slice(start: number, end: number): Blob;
}

export interface RunUploadOptions {
  api?: UploadApi;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
  /** Read from the local <video> preview's `loadedmetadata` — the video row's duration. */
  meta?: { durationMs?: number; width?: number; height?: number };
  /** Injected so tests do not wait out the backoff. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * A 4xx will fail identically on the next attempt (413 too large, 415 wrong type, 404 not yours,
 * 409 the upload is gone) — retrying it only delays the error the wizard has to show. Timeouts,
 * rate limits and everything from 500 up are worth another go, and so is a bare network failure
 * (a `TypeError` from fetch, which is what a dropped connection looks like).
 */
function retriable(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  const status = e.problem.status;
  return status === undefined || status >= 500 || status === 408 || status === 429;
}

/**
 * create → parts (UPLOAD_PARALLEL at a time, PART_ATTEMPTS each) → complete.
 *
 * Rejects with `UploadAbortedError` if the caller cancels — and only then do we call
 * `api.abort`, because only then do we know the upload will never be finished. A part that
 * exhausts its attempts rejects with the underlying error and leaves the multipart upload open
 * on purpose: see the module docblock (no resume, the reaper cleans up, "try again" is a new
 * upload).
 */
export async function runUpload(
  file: FileLike,
  opts: RunUploadOptions = {},
): Promise<MediaUploadCompleteResponse> {
  const net = opts.api ?? liveApi;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const emit = opts.onProgress ?? (() => {});
  // Cancelled before we opened anything: there is nothing on the server to abort.
  if (opts.signal?.aborted) throw new UploadAbortedError();

  // `cancelled` is the user; `failure` is a part that gave up. Either stops the pool, and the
  // shared controller cancels the requests still in flight so we do not upload bytes nobody
  // will complete — but only `cancelled` tells the server, so the two are tracked apart.
  //
  // Both are set up BEFORE the create call, not after it. The wizard shows Cancel the moment it
  // sets phase 'uploading', which is a frame after this function starts and long before `create`
  // resolves — an INSERT, an R2 createMultipartUpload and a second INSERT, from a browser to
  // Frankfurt. A listener registered after the await simply misses a cancel in that window: the
  // upload would then run to completion, `videoCreate` would make the video, and the wizard would
  // navigate to a video the learner cancelled. Same for the unmount path, which aborts the signal.
  let cancelled = false;
  let failure: unknown = null;
  const ctl = new AbortController();
  const onAbort = () => {
    cancelled = true;
    ctl.abort();
  };
  opts.signal?.addEventListener('abort', onAbort);

  try {
    return await runOpenUpload();
  } finally {
    opts.signal?.removeEventListener('abort', onAbort);
  }

  async function runOpenUpload(): Promise<MediaUploadCompleteResponse> {
    const created = await net.create({
      filename: file.name,
      bytes: file.size,
      contentType: file.type,
      ...(opts.meta?.durationMs !== undefined ? { durationMs: opts.meta.durationMs } : {}),
      ...(opts.meta?.width !== undefined ? { width: opts.meta.width } : {}),
      ...(opts.meta?.height !== undefined ? { height: opts.meta.height } : {}),
    });
    // Cancelled while create was in flight: the upload exists on the server now, so tell it.
    if (cancelled) {
      await net.abort(created.uploadId).catch(() => {});
      throw new UploadAbortedError();
    }
    const plans = planParts(file.size, created.partSize);

    let progress = uploadReduce(UPLOAD_IDLE, {
      type: 'start',
      totalBytes: file.size,
      parts: plans.length,
      at: Date.now(),
    });
    emit(progress);

    const uploadOne = async (plan: PartPlan): Promise<MediaPart> => {
      const blob = file.slice(plan.start, plan.end);
      let last: unknown;
      for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt++) {
        if (cancelled) throw new UploadAbortedError();
        // Another part has already given up: this run is over, so stop spending attempts on it.
        if (failure !== null) throw failure;
        try {
          return await net.part(created.uploadId, plan.n, blob, { signal: ctl.signal });
        } catch (e) {
          if (cancelled) throw new UploadAbortedError();
          last = e;
          if (attempt === PART_ATTEMPTS || failure !== null || !retriable(e)) break;
          await sleep(PART_RETRY_BASE_MS * 2 ** (attempt - 1));
        }
      }
      throw last;
    };

    const parts: MediaPart[] = new Array<MediaPart>(plans.length);
    {
      let next = 0;
      const worker = async (): Promise<void> => {
        while (!cancelled && failure === null) {
          const i = next++;
          if (i >= plans.length) return;
          try {
            parts[i] = await uploadOne(plans[i]);
          } catch (e) {
            // The first hard failure wins; an abort has its own flag and reports nothing here.
            if (!cancelled && failure === null) {
              failure = e;
              ctl.abort();
            }
            return;
          }
          progress = uploadReduce(progress, {
            type: 'part',
            bytes: plans[i].bytes,
            at: Date.now(),
          });
          emit(progress);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_PARALLEL, plans.length) }, () => worker()),
      );

      if (cancelled) {
        // Best effort: the upload is doomed either way, and the reaper is the backstop.
        await net.abort(created.uploadId).catch(() => {});
        throw new UploadAbortedError();
      }
      if (failure !== null) throw failure;
    }

    // `parts` is already ascending by n: plans are, and slot i holds part i+1.
    const done = await net.complete(created.uploadId, parts);
    // And once more: a cancel during `complete` must not resolve into a video. The object is real
    // by now, so there is nothing to abort — the server's reaper collects a completed upload that
    // never became a video, which is exactly this case.
    if (cancelled) throw new UploadAbortedError();
    return done;
  }
}
